/**
 * Modelos — qué modelo usa cada agente cuando no le dices otro.
 *
 * La primera sección del estudio en el documento del Editor Studio (11-sep-2026). Cinco
 * decisiones, no dieciocho: por defecto, coach, escritor fantasma, editor de desarrollo, corrector
 * y publicación. El precio va delante, por millón de tokens de entrada y de salida, porque lo pagas
 * tú y la diferencia entre dos modelos puede ser de veinte veces.
 *
 * La clave de OpenRouter no pasa por aquí: vive en tu equipo y se pone en Ajustes.
 */
import { useEffect, useMemo, useState } from "react";
import { PROMESA_PRIVACIDAD, catalogoModelos, guardarEsfuerzos, guardarModelos, miles, modelosAsignados } from "../api";
import type { ModeloCatalogo, ModelosEstado } from "../api";

const precio = (n: number | null) =>
  n == null ? "—" : n === 0 ? "gratis" : n < 0.01 ? "<0,01 $" : `${n.toLocaleString("es-ES", { maximumFractionDigits: 2 })} $`;

/** OpenRouter nombra a unos «Anthropic: Claude Sonnet 5» y a otros «Claude Opus 5»: fuera el prefijo. */
const limpio = (n: string) => n.replace(/^[^:]{1,40}:\s*/, "");

/** Los niveles de esfuerzo de OpenRouter, con su nombre para el autor. «ultra» no existe: el tope es «max». */
const NIVEL: Record<string, string> = { none: "sin razonar", minimal: "mínimo", low: "bajo", medium: "medio", high: "alto", xhigh: "muy alto", max: "máximo" };
/** Los grupos que reescriben el manuscrito: pasadas (editor, fantasma) y corrección. */
const REESCRIBEN = new Set(["editor", "ghostwriter", "copyeditor"]);

const INICIALES: Record<string, string> = { "*": "★", coach: "CO", ghostwriter: "EF", editor: "ED", copyeditor: "CR", publicar: "PU" };

type Catalogo = { modelos: ModeloCatalogo[]; fuente: string; motivo?: string };

export default function Modelos() {
  const [estado, setEstado] = useState<ModelosEstado | null>(null);
  const [cat, setCat] = useState<Catalogo | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);   // el grupo cuyo selector está abierto
  const [busca, setBusca] = useState("");
  const [fallo, setFallo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState<string | null>(null);

  useEffect(() => {
    modelosAsignados().then(setEstado).catch((e) => setFallo(
      /404/.test(String(e?.message))
        ? "El HumanInk de este ordenador es anterior a esta pantalla. Actualízalo desde el aviso de arriba y vuelve."
        : "No he podido leer tus modelos. Recarga la página; si sigue igual, reinstala HumanInk.",
    ));
    catalogoModelos().then(setCat).catch(() => setCat({ modelos: [], fuente: "sin-red" }));
  }, []);

  const porId = useMemo(() => new Map((cat?.modelos ?? []).map((m) => [m.id, m])), [cat]);
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const lista = cat?.modelos ?? [];
    return (q ? lista.filter((m) => m.id.toLowerCase().includes(q) || m.nombre.toLowerCase().includes(q)) : lista).slice(0, 12);
  }, [cat, busca]);

  const asignar = async (grupo: string, id: string | null, modelo?: ModeloCatalogo) => {
    // M-08: elegir uno que guarda tu texto avisa antes de guardar. No se bloquea: es su clave y su
    // decisión, pero no se cambia la promesa sin decírselo.
    if (modelo?.zdr === false && !confirm(
      `${limpio(modelo.nombre)} no se puede servir sin retención: ese proveedor puede guardar el fragmento que le mandes.\n\n`
      + "Con los demás modelos el motor exige que no lo guarden ni entrenen con él. ¿Lo asignas igualmente?",
    )) return;
    setGuardando(grupo); setFallo(null);
    try {
      setEstado(await guardarModelos({ [grupo]: id }));
      setAbierto(null); setBusca("");
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setGuardando(null);
    }
  };

  if (!estado) {
    return <section className="carta"><p className="vacio" style={fallo ? { color: "var(--acc)" } : undefined}>{fallo ?? "Leyendo tus modelos…"}</p></section>;
  }

  const efectivo = (g: string) => estado.asignados[g] || (g === "*" ? estado.defecto : estado.asignados["*"] || estado.defecto);
  const esforzar = async (grupo: string, nivel: string) => {
    setGuardando(grupo); setFallo(null);
    try { setEstado(await guardarEsfuerzos({ [grupo]: nivel || null })); }
    catch (e) { setFallo((e as Error).message); }
    finally { setGuardando(null); }
  };

  return (
    <>
      <div className="saludo">
        <h1>Modelos</h1>
        <p>Qué modelo usa cada agente cuando no le dices otro. Lo pagas tú con tu llave de OpenRouter, así que aquí ves lo que cuesta cada uno.</p>
      </div>

      {fallo && <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>}
      {cat?.fuente === "sin-red" && (
        <section className="carta">
          <p className="vacio">No he podido traer el catálogo de OpenRouter ({cat.motivo ?? "sin red"}). Tus modelos siguen funcionando; para cambiarlos, vuelve cuando tengas conexión.</p>
        </section>
      )}

      <section className="carta">
        <h2>Por agente<span className="sub">precio por millón de tokens · entrada / salida</span></h2>
        <p className="vacio">{PROMESA_PRIVACIDAD} Los que cumplen salen con ✓; los que no, con ⚠.</p>
        {estado.niveles && (
          <p className="vacio">
            <b>Esfuerzo</b> es cuánto razona el modelo antes de contestar. Más esfuerzo trabaja mejor lo difícil y
            cuesta más, porque el razonamiento se paga como salida.{" "}
            {estado.esfuerzoReescritura
              ? `Si no eliges, las pasadas y la corrección van en «${NIVEL[estado.esfuerzoReescritura] ?? estado.esfuerzoReescritura}» y lo demás en lo que traiga el modelo.`
              : "Si no eliges, cada modelo razona lo que trae por defecto."}
          </p>
        )}
        <div className="relevo">
          {Object.entries(estado.grupos).map(([g, nombre], i) => {
            const id = efectivo(g);
            const m = porId.get(id);
            const propio = !!estado.asignados[g];
            return (
              // Una fila por agente con su acción a la derecha. Antes cada «Cambiar» vivía en su propia
              // caja a lo ancho: seis cajas con un botón pesaban más que lo que decían.
              <div key={g} style={i ? { borderTop: "1px solid var(--linea2)" } : undefined}>
                <div className="paso" style={{ gridTemplateColumns: "26px 1fr auto", alignItems: "center" }}>
                  <span className="cara" style={propio ? { background: "var(--acc-suave)", color: "var(--acc)" } : undefined}>{INICIALES[g] ?? "·"}</span>
                  <span className="qu">
                    <b>{nombre}</b>
                    <span>{m ? limpio(m.nombre) : id}</span>
                    <span className="meta">
                      {m ? `${precio(m.entrada)} / ${precio(m.salida)}${m.contexto ? ` · ${miles(m.contexto)} de contexto` : ""}` : id}
                      {!propio && g !== "*" ? " · el de por defecto" : ""}
                    </span>
                    {estado.niveles && (
                      <label className="meta" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        Esfuerzo
                        <select className="escribir" style={{ padding: "2px 6px", width: "auto" }} disabled={guardando === g}
                                value={estado.esfuerzos?.[g] ?? ""} onChange={(e) => esforzar(g, e.target.value)}>
                          <option value="">
                            {REESCRIBEN.has(g) && estado.esfuerzoReescritura ? `pasadas y corrección en ${NIVEL[estado.esfuerzoReescritura]}; lo demás, el del modelo` : "el del modelo"}
                            {m?.esfuerzoDefecto ? ` (${NIVEL[m.esfuerzoDefecto] ?? m.esfuerzoDefecto})` : ""}
                          </option>
                          {/* Solo los que el motor sabe guardar (equipo rojo 1.6.24: «minimal» daba error al elegirlo). */}
                          {(m?.esfuerzos ?? estado.niveles).filter((n) => estado.niveles!.includes(n) && n in NIVEL).map((n) => <option key={n} value={n}>{NIVEL[n]}</option>)}
                        </select>
                      </label>
                    )}
                  </span>
                  <span style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {propio && g !== "*" && (
                      <button className="chip" onClick={() => asignar(g, null)} disabled={guardando === g}>Usar el de por defecto</button>
                    )}
                    <button className={"chip" + (abierto === g ? " puesto" : "")} onClick={() => { setAbierto(abierto === g ? null : g); setBusca(""); }}>
                      {abierto === g ? "Cerrar" : "Cambiar"}
                    </button>
                  </span>
                </div>
                {abierto === g && (
                  <div className="adjuntos" style={{ margin: "8px 0 12px" }}>
                    <input
                      className="escribir" autoFocus style={{ minWidth: 260 }}
                      placeholder={`Busca entre ${cat?.modelos.length ?? 0} modelos…`}
                      value={busca} onChange={(e) => setBusca(e.target.value)}
                    />
                    {filtrados.map((x) => (
                      <button
                        key={x.id} className={"chip" + (x.id === id ? " puesto" : "")}
                        onClick={() => asignar(g, x.id, x)} disabled={guardando === g}
                        title={`${x.proveedor} · ${x.id}${x.zdr === false ? " · guarda tu texto" : x.zdr ? " · retención cero" : ""}`}
                      >
                        {limpio(x.nombre)} · {precio(x.entrada)} / {precio(x.salida)}
                        {/* M-08: el que no cumple sale marcado ANTES de elegirlo. */}
                        {x.zdr === false ? " · ⚠" : x.zdr ? " · ✓" : ""}
                      </button>
                    ))}
                    {!filtrados.length && <span className="vacio">Ningún modelo encaja con «{busca}».</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="carta">
        <h2>Tu llave<span className="sub">se queda en tu equipo</span></h2>
        <p className="vacio">
          La llave de OpenRouter vive en este ordenador y no va a ningún servidor nuestro; se cambia en
          Ajustes. Lo que eliges aquí, en cambio, irá con tu cuenta en cuanto la nube esté lista, para
          que no tengas que repetirlo si cambias de equipo.
        </p>
      </section>
    </>
  );
}
